import { zodResolver } from '@hookform/resolvers/zod';
import { ForwardRefRenderFunction, forwardRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { InfoSpotDetailsFragment } from '../../../../../../../generated/graphql';
import { EnrichedParentStopPlace } from '../../../../../../../types';
import { FormActionButtons } from '../../../../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../../../../common/Forms/NavigationBlocker';
import { TerminalInfoSpotFormState, terminalInfoSpotSchema } from '../Types';
import { TerminalInfoSpotFormFields } from './TerminalInfoSpotsFormFields';

const testIds = {
  infoSpot: 'TerminalInfoSpotsForm::infoSpot',
};

type TerminalInfoSpotsFormProps = {
  readonly className?: string;
  readonly defaultValues: TerminalInfoSpotFormState;
  readonly infoSpot?: InfoSpotDetailsFragment;
  readonly onSubmit: (state: TerminalInfoSpotFormState) => void;
  readonly terminal: EnrichedParentStopPlace;
  readonly onCancel: () => void;
  readonly testIdPrefix: string;
  readonly addNewButton?: React.ReactNode;
};

const TerminalInfoSpotsFormComponent: ForwardRefRenderFunction<
  HTMLFormElement,
  TerminalInfoSpotsFormProps
> = (
  {
    className,
    defaultValues,
    onSubmit,
    terminal,
    infoSpot,
    onCancel,
    testIdPrefix,
    addNewButton,
  },
  ref,
) => {
  const methods = useForm<TerminalInfoSpotFormState>({
    defaultValues,
    resolver: zodResolver(terminalInfoSpotSchema),
  });
  const { setValue, getValues, handleSubmit, formState } = methods;
  useDirtyFormBlockNavigation(formState, 'TerminalInfoSpotsForm');
  const { isDirty } = formState;

  const isNewInfoSpot = !getValues('infoSpotId');
  // Form is considered dirty if there are unsaved changes or if a new info spot has been added
  const hasChanges = isDirty || isNewInfoSpot;

  const onRemoveInfoSpot = () => {
    if (!getValues('infoSpotId')) {
      return;
    }

    const newToBeDeleted = !getValues('toBeDeleted');
    setValue(`toBeDeleted`, newToBeDeleted, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <FormProvider {...methods}>
      <form className={className} onSubmit={handleSubmit(onSubmit)} ref={ref}>
        <div data-testid={testIds.infoSpot} className="mt-0">
          <TerminalInfoSpotFormFields
            infoSpot={infoSpot}
            terminal={terminal}
            onRemove={onRemoveInfoSpot}
          />
        </div>
        <FormActionButtons
          onCancel={onCancel}
          testIdPrefix={testIdPrefix}
          isDisabled={!hasChanges || methods.formState.isSubmitting}
          isSubmitting={methods.formState.isSubmitting}
          addNewButton={addNewButton}
          variant="infoContainer"
          className="mx-0 my-0"
        />
      </form>
    </FormProvider>
  );
};

export const TerminalInfoSpotsForm = forwardRef(TerminalInfoSpotsFormComponent);
